// Me / Profile — TRIMIE Hi-Fi v1 (per docs/design-spec.md §4.13 + Hi-Fi PDF p.8)
//
// Header: title "Profile" + bell action.
// Top row: 56×56 avatar + name + small email + Edit pill (opens inline name edit).
// Stats row: 3 tiles — ITEMS / LOOKS / POLLS.
// Settings list (each row has a leading icon):
//   • Profile photo  — opens upload sheet (decorative avatar)
//   • My body photos — Soon (will hold a saved default body photo for try-on)
//   • Sizing & fit   — Soon
//   • Privacy        — Soon
//   • Subscription   — Free
//   • Notifications  — Soon
// Bottom: outline Sign out.

import { useState, useRef, useEffect } from 'react';
import api from './api';
import CameraCapture from './CameraCapture';
import Header from './components/Header';
import { compressImage } from './utils/compressImage';
import { resolveUrl } from './utils/media';

const SIZING_KEY = 'trimie_sizing';

const Chev = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const BellIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9a6 6 0 0 1 12 0v5l2 3H4l2-3V9z" />
    <path d="M9 19a3 3 0 0 0 6 0" />
  </svg>
);

// Icons for settings rows — kept inline so we don't pull a whole icon set.
const ICONS = {
  photo: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="12" r="2.5" />
      <path d="M21 17l-5-5-7 7" />
    </svg>
  ),
  body: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="6" r="3" />
      <path d="M7 22v-7l-2-3 3-3h8l3 3-2 3v7" />
    </svg>
  ),
  sizing: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="8" rx="1.5" />
      <path d="M7 8v3M11 8v4M15 8v3M19 8v2" />
    </svg>
  ),
  priv: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
    </svg>
  ),
  sub: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l3 6 6 .9-4.4 4.3 1 6.1L12 16.5 6.4 19.3l1-6.1L3 8.9 9 8z" />
    </svg>
  ),
  notif: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9a6 6 0 0 1 12 0v5l2 3H4l2-3V9z" />
      <path d="M9 19a3 3 0 0 0 6 0" />
    </svg>
  ),
};

function loadSizing() {
  try { return JSON.parse(localStorage.getItem(SIZING_KEY) || '{}'); }
  catch { return {}; }
}

export default function ProfilePage({ user, onUserUpdate, onLogout, closetCount, tryonCount, onNav }) {
  // Photo sheets: 'profile' = decorative avatar, 'body' = try-on model input.
  // null means no sheet open. We keep them as one piece of state so only one
  // sheet can be open at a time.
  const [photoSheet, setPhotoSheet] = useState(null); // null | 'profile' | 'body'
  const [showCamera, setShowCamera] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const profileInputRef = useRef(null);
  const bodyInputRef = useRef(null);

  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);

  const avatarUrl = resolveUrl(user?.profilePhotoUrl);
  const bodyPhotoUrl = resolveUrl(user?.bodyPhotoUrl);
  const displayName =
    user?.displayName ||
    (user?.username && user.username.trim()) ||
    (user?.email ? user.email.split('@')[0] : 'you');

  useEffect(() => { setDraftName(user?.username || ''); }, [user?.username]);

  const sizing = loadSizing();
  const sizingSummary = ['tops', 'bottoms', 'dresses']
    .map(k => sizing[k])
    .filter(Boolean)
    .join(' · ');

  // ── actions ──────────────────────────────────────────────────────────────
  // kind: 'profile' for the decorative avatar, 'body' for the try-on model input.
  // Photos are compressed + JPEG-encoded client-side first so HEIC and >2MB
  // files don't bounce off the backend's serializer.
  const uploadPhoto = async (file, kind) => {
    setUploading(true);
    setError(null);
    let prepared;
    try { prepared = await compressImage(file); }
    catch (err) {
      setUploading(false);
      setError(err.message || 'Could not read that image.');
      return;
    }
    const form = new FormData();
    form.append('profile_photo', prepared);
    form.append('kind', kind);
    try {
      const res = await api.post('/auth/profile/upload-profile/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data?.data || {};
      if (onUserUpdate) {
        onUserUpdate({
          ...user,
          ...(data.profilePhotoUrl ? { profilePhotoUrl: data.profilePhotoUrl } : {}),
          ...(data.bodyPhotoUrl ? { bodyPhotoUrl: data.bodyPhotoUrl } : {}),
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      setPhotoSheet(null);
    }
  };

  const removePhoto = async (kind) => {
    const label = kind === 'body' ? 'body photo' : 'profile photo';
    if (!window.confirm(`Remove your ${label}?`)) return;
    setUploading(true);
    setError(null);
    try {
      const field = kind === 'body' ? 'bodyPhotoUrl' : 'profilePhotoUrl';
      const res = await api.patch('/auth/profile/', { [field]: null });
      const updated = res.data?.data;
      if (updated && onUserUpdate) onUserUpdate(updated);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not remove photo.');
    } finally {
      setUploading(false);
      setPhotoSheet(null);
    }
  };

  const saveName = async () => {
    const value = draftName.trim();
    if (value === (user?.username || '').trim()) { setEditingName(false); return; }
    setSavingName(true);
    setError(null);
    try {
      const res = await api.patch('/auth/profile/', { username: value });
      const updated = res.data?.data;
      if (updated && onUserUpdate) onUserUpdate(updated);
      setEditingName(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update name.');
    } finally {
      setSavingName(false);
    }
  };

  // ── settings rows ────────────────────────────────────────────────────────
  const rows = [
    {
      key: 'photo',
      icon: ICONS.photo,
      title: 'Profile photo',
      sub: avatarUrl ? 'Tap to change' : 'Add one',
      onClick: () => setPhotoSheet('profile'),
      enabled: true,
    },
    {
      key: 'body',
      icon: ICONS.body,
      title: 'My body photo',
      sub: bodyPhotoUrl ? 'Tap to change' : 'Add one for try-on',
      onClick: () => setPhotoSheet('body'),
      enabled: true,
    },
    {
      key: 'sizing',
      icon: ICONS.sizing,
      title: 'Sizing & fit',
      sub: sizingSummary || 'Not set',
      onClick: () => {},
      enabled: false,
    },
    { key: 'priv',  icon: ICONS.priv,  title: 'Privacy',      sub: 'Photos private',     onClick: () => {}, enabled: false },
    { key: 'sub',   icon: ICONS.sub,   title: 'Subscription', sub: 'Free',               onClick: () => {}, enabled: false },
    { key: 'notif', icon: ICONS.notif, title: 'Notifications', sub: 'Soon',              onClick: () => {}, enabled: false },
  ];

  return (
    <div className="screen">
      {showCamera && (
        <CameraCapture
          label={photoSheet === 'body' ? 'Take a full-body photo' : 'Take a selfie'}
          onCapture={(f) => {
            const kind = photoSheet || 'profile';
            setShowCamera(false);
            uploadPhoto(f, kind);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}

      <Header
        title="Profile"
        action={{ icon: BellIcon, ariaLabel: 'Notifications' }}
      />

      <div className="profile-body">
        {/* Top row — avatar + name + Edit pill */}
        <div className="profile-header">
          <button
            className="profile-avatar profile-avatar-btn"
            onClick={() => setPhotoSheet('profile')}
            aria-label="Change profile photo"
            type="button"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" />
            ) : (
              <div className="profile-avatar-initial">
                {(displayName || '?').trim().charAt(0).toUpperCase()}
              </div>
            )}
            {uploading && (
              <div className="profile-avatar-overlay">
                <span className="spinner" style={{ color: '#fff' }} />
              </div>
            )}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="profile-name">{displayName}</div>
            <div className="profile-email">{user?.email || ''}</div>
          </div>
          <button
            className="profile-edit-pill"
            onClick={() => setEditingName(true)}
            type="button"
          >
            Edit
          </button>
        </div>

        {/* Stats row */}
        <div className="profile-stats-row">
          <div className="profile-stat">
            <div className="profile-stat-num">{closetCount ?? 0}</div>
            <div className="profile-stat-label">ITEMS</div>
          </div>
          <div className="profile-stat">
            <div className="profile-stat-num">{tryonCount ?? 0}</div>
            <div className="profile-stat-label">LOOKS</div>
          </div>
          <div className="profile-stat">
            <div className="profile-stat-num">0</div>
            <div className="profile-stat-label">POLLS</div>
          </div>
        </div>

        {error && <div className="alert error">{error}</div>}

        {/* Inline name editor */}
        {editingName && (
          <div className="profile-list">
            <div className="profile-row" style={{ cursor: 'default' }}>
              <div style={{ flex: 1, marginRight: 10 }}>
                <div className="profile-row-title" style={{ marginBottom: 6 }}>Display name</div>
                <input
                  type="text"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder={user?.email ? user.email.split('@')[0] : 'your name'}
                  maxLength={50}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveName();
                    if (e.key === 'Escape') { setDraftName(user?.username || ''); setEditingName(false); }
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="profile-row-action primary" onClick={saveName} disabled={savingName}>
                  {savingName ? '…' : 'Save'}
                </button>
                <button type="button" className="profile-row-action" onClick={() => { setDraftName(user?.username || ''); setEditingName(false); }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings list with icons */}
        <div className="profile-list">
          {rows.map((r) => <ProfileRow key={r.key} row={r} />)}
        </div>

        <button className="btn-outline" onClick={onLogout}>Sign out</button>
      </div>

      {/* Photo bottom sheet — same UI for both profile and body, differs in
          title, current-photo source, and which kind we upload. */}
      {photoSheet && (
        <div className="t-sheet-backdrop" onClick={() => setPhotoSheet(null)}>
          <div className="t-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="t-sheet-handle" />
            <div className="t-sheet-title">
              {photoSheet === 'body' ? 'Body photo' : 'Profile photo'}
            </div>
            {photoSheet === 'body' && (
              <div className="t-sheet-hint">
                Full-body, daylight, arms relaxed. Used as the model in try-on.
              </div>
            )}
            <button className="btn-outline btn-lg" onClick={() => setShowCamera(true)}>Camera</button>
            <button
              className="btn-outline btn-lg"
              onClick={() => (photoSheet === 'body' ? bodyInputRef : profileInputRef).current?.click()}
            >
              Library
            </button>
            {photoSheet === 'body' && (
              <button
                className="btn-outline btn-lg"
                onClick={() => { setPhotoSheet(null); onNav('avatar-builder'); }}
              >
                Build an avatar instead
              </button>
            )}
            {((photoSheet === 'profile' && avatarUrl) ||
              (photoSheet === 'body' && bodyPhotoUrl)) && (
              <button
                className="btn-outline btn-lg"
                onClick={() => removePhoto(photoSheet)}
                style={{ color: 'var(--t-purple-deep)' }}
              >
                Remove current photo
              </button>
            )}
          </div>
        </div>
      )}

      <input ref={profileInputRef} type="file" accept="image/*" hidden
             onChange={(e) => { const f = e.target.files[0]; if (f) uploadPhoto(f, 'profile'); }} />
      <input ref={bodyInputRef} type="file" accept="image/*" hidden
             onChange={(e) => { const f = e.target.files[0]; if (f) uploadPhoto(f, 'body'); }} />
    </div>
  );
}

function ProfileRow({ row }) {
  const Inner = (
    <>
      <span className="profile-row-icon">{row.icon}</span>
      <div className="profile-row-content">
        <div className="profile-row-title">{row.title}</div>
        {row.sub && <div className="profile-row-sub">{row.sub}</div>}
      </div>
      <span className="chev">{Chev}</span>
    </>
  );
  if (row.enabled) {
    return <button className="profile-row" onClick={row.onClick} type="button">{Inner}</button>;
  }
  return <div className="profile-row disabled" aria-disabled="true">{Inner}</div>;
}
